/**
 * Provider-specific credential sections.
 *
 * Ported 1:1 from wizard-framework/src/lib/wizard/stepTypes/components/credSections/
 * Each section matches the exact UI behavior of the legacy component.
 *
 * Routing decision (from credSections/index.js):
 *   isOauth (credentials === ["Oauth"])  → OAuthSection (Salesforce/Blackboard)
 *   isDocuSign (type === "DocuSign")     → DocuSignSection
 *   isEdFi (type.lower() === "edfi")     → EdFiSection
 *   isPeopleSoftV2 (type === "PeopleSoftV2") → PeopleSoftSection
 *   isFlatFile (type === "LocalFile")    → FlatFileSection
 *   isBVA (type === "BrightSpaceValenceApi") → OAuthKeyToggleSection
 *   isGoogle (type.startsWith("Google")) → GoogleSection
 *   isSFTP (type === "SFTP")            → SFTPSection
 *   isWebSocket (type === "LingkWebSocket") → WebSocketSection
 *   isZoom (type === "Zoom")            → OAuthKeyToggleSection
 *   isCanvasOAuth2 (type === "CanvasOAuth2") → OAuthKeyToggleSection
 *   else                                → DefaultSection
 *     + if isAdapter → AdapterSecretSection appended
 */
import { useApp } from '@/contexts';

import { useEffect, useRef, useState } from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  Link,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';

import { useAppDispatch } from '@/app/hooks';
import { CopyButton, DeleteConfirmDialog } from '@/components/common';
import { useGetTenantQuery } from '@/features/tenants/api/tenantsApi';
import type { Credential, IntegrationProvider } from '@/types';
import { logger } from '@/utils/logger';

import {
  environmentsApi,
  useDeleteFileMutation,
  useGenerateAdapterSecretMutation,
  useGenerateCertsMutation,
  useGetBvaOAuthUrlMutation,
  useGetCredentialsQuery,
  useGetFileSchemaCountQuery,
  useGetFileSchemaQuery,
  useGetOAuthUrlMutation,
  useGetUploadPolicyMutation,
  useLazyCheckOAuthCredsQuery,
  useLazyGetDownloadPolicyQuery,
  useSaveCredentialsMutation,
} from '../api/environmentsApi';
import { OAUTH_TYPE_GUIDS } from '../constants/oauth';
import { useOAuthFlow } from '../hooks/useOAuthFlow';
import { getOAuthProviderLabel } from '../utils/credentialTypeMapping';
import {
  AnimatedDots,
  CredField,
  CredTextarea,
  CredToggle,
  formatBytes,
  labelSx,
  oauthBtnSx,
  unCamelize,
  wideInputSx,
} from './credentials/shared';

// ════════════════════════════════════════════════════
//  TYPES
// ════════════════════════════════════════════════════

export interface CredentialSectionProps {
  provider: IntegrationProvider;
  creds: Record<string, string>;
  onChange: (name: string, value: string) => void;
  showPass: boolean;
  toggleShowPass: () => void;
  envId: string;
  envName: string;
  connectorKey: string;
  existingCredentials: Credential[];
  connectionSuccess?: boolean;
  isTesting?: boolean;
}

// ════════════════════════════════════════════════════
//  1. OAUTH SECTION (Salesforce / Blackboard)
//  Matches: credSections/oauth.js
// ════════════════════════════════════════════════════

export const OAuthSection = ({
  creds,
  provider,
  envName,
  connectorKey,
  onChange,
  connectionSuccess,
  isTesting,
}: CredentialSectionProps) => {
  const { appId, tenantId } = useApp();
  const { userName, isSandBox, instanceUrl } = creds as Record<string, string>;
  const providerType = provider.type;
  const [checkOAuthCreds] = useLazyCheckOAuthCredsQuery();

  const handleOAuthComplete = () => {
    onChange('userName', '_');

    const params = {
      tenantId,
      appId,
      providerType: getOAuthProviderLabel(provider.type),
      envName,
      connectionKey: connectorKey,
    };

    checkOAuthCreds(params)
      .unwrap()
      .then((credential) => {
        const json = credential?.credentialsJson as Record<string, string> | undefined;
        if (json) {
          if (json.userName) onChange('userName', json.userName);
          if (json.instanceUrl) onChange('instanceUrl', json.instanceUrl);
          if (json.isSandBox !== undefined) onChange('isSandBox', json.isSandBox);
        } else {
          logger.warn('[Salesforce OAuth] No credentialsJson in response');
        }
      })
      .catch((err) => {
        logger.error('[Salesforce OAuth] checkOAuthCreds failed:', err);
        onChange('userName', '');
      });
  };

  const { oauthUrl, isAuthorizing, isGettingUrl, generateUrl, startOauth } = useOAuthFlow({
    typeGuid: OAUTH_TYPE_GUIDS.SALESFORCE,
    tenantId,
    appId,
    envName,
    connectionKey: connectorKey,
    apiEndpoint: 'sfAgent',
    onOAuthComplete: handleOAuthComplete,
  });

  return (
    <Box>
      {/* Authorized user info (matches oauth.js lines 61-77) */}
      {userName && (
        <Box sx={{ mb: 2 }}>
          {(() => {
            if (userName === '_') return <AnimatedDots text="Authorizing" />;
            if (instanceUrl && instanceUrl !== '') {
              return (
                <Typography sx={{ fontSize: '13px' }}>
                  Authorized with account <strong>{userName}</strong> for the instance
                  <br />
                  with URL: <strong>{instanceUrl}</strong>
                </Typography>
              );
            }
            return (
              <Typography sx={{ fontSize: '13px' }}>
                Authorized with account <strong>{userName}</strong> for the instance
                <br />
                with URL: <AnimatedDots text="Loading" />
              </Typography>
            );
          })()}
        </Box>
      )}

      {/* Prompt text */}
      <Typography sx={{ fontSize: '13px', mb: 1.5 }}>
        {userName
          ? 'Connect to a different Salesforce Org:'
          : 'Are you connecting to a Sandbox or Production Org?'}
      </Typography>

      {/* Sandbox / Production buttons (matches oauth.js lines 84-107) */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Button
          variant="contained"
          size="small"
          sx={oauthBtnSx}
          onClick={() => generateUrl('sandbox')}
          disabled={isGettingUrl}
        >
          Sandbox
        </Button>
        {isSandBox && isAuthorizing && <CircularProgress size={16} />}
        <Button
          variant="contained"
          size="small"
          sx={{ ...oauthBtnSx, ml: 2 }}
          onClick={() => generateUrl('production')}
          disabled={isGettingUrl}
        >
          Production
        </Button>
        {!isSandBox && isAuthorizing && <CircularProgress size={16} />}
        {isTesting && <CircularProgress size={20} />}
        {connectionSuccess && !isTesting && (
          <CheckCircleIcon sx={{ color: '#00853E', fontSize: 28 }} />
        )}
      </Box>

      {/* Getting URL spinner */}
      {isGettingUrl && (
        <Box sx={{ my: 1 }}>
          <CircularProgress size={16} />
        </Box>
      )}

      {/* OAuth link (matches oauth.js lines 174-187) */}
      {oauthUrl && (
        <Box sx={{ my: 1 }}>
          <Typography sx={{ fontSize: '13px' }}>
            <strong>
              <Box
                component="span"
                sx={{ cursor: 'pointer', color: '#0066cc', textDecoration: 'underline' }}
                onClick={startOauth}
              >
                Click Here
              </Box>
            </strong>{' '}
            to authorize your {providerType} Org.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ════════════════════════════════════════════════════
//  2. DOCUSIGN SECTION
//  Matches: credSections/docuSign.js
// ════════════════════════════════════════════════════

export const DocuSignSection = ({
  creds,
  provider,
  envName,
  connectorKey,
  onChange,
  connectionSuccess,
  isTesting,
}: CredentialSectionProps) => {
  const { appId, tenantId } = useApp();
  const { userName } = creds;
  const dsName = creds.name;
  const dsAccount = creds.accountId;
  const { isSandbox } = creds;
  const dsAccountName = creds.accountName;
  const providerType = provider.type;
  const [checkOAuthCreds] = useLazyCheckOAuthCredsQuery();

  const handleOAuthComplete = () => {
    onChange('userName', '_');
    const fetchParams = {
      tenantId,
      appId,
      providerType: getOAuthProviderLabel(provider.type),
      envName,
      connectionKey: connectorKey,
    };
    checkOAuthCreds(fetchParams)
      .unwrap()
      .then((credential) => {
        const json = credential?.credentialsJson as Record<string, string> | undefined;
        if (json) {
          if (json.userName) onChange('userName', json.userName);
          if (json.name) onChange('name', json.name);
          if (json.accountId) onChange('accountId', json.accountId);
          if (json.accountName) onChange('accountName', json.accountName);
          if (json.isSandbox !== undefined) onChange('isSandbox', json.isSandbox);
        }
      })
      .catch(() => {
        onChange('userName', '');
      });
  };

  const {
    oauthUrl: dsOauthUrl,
    isAuthorizing,
    isGettingUrl,
    generateUrl: handleGenerateUrl,
    startOauth,
  } = useOAuthFlow({
    typeGuid: OAUTH_TYPE_GUIDS.DOCUSIGN,
    tenantId,
    appId,
    envName,
    connectionKey: connectorKey,
    onOAuthComplete: handleOAuthComplete,
  });

  return (
    <Box>
      {/* Authorized user info (matches docuSign.js lines 73-101) */}
      {userName && (
        <Box sx={{ mb: 2 }}>
          {userName === '_' ? (
            <AnimatedDots text="Authorizing" />
          ) : (
            <Box sx={{ fontSize: '13px' }}>
              <Typography sx={{ fontSize: '13px' }}>
                Authorized with <strong>{isSandbox ? 'Developer' : 'Production'}</strong>{' '}
                environment
              </Typography>
              <Box sx={{ mt: 0.5, fontSize: '13px', lineHeight: 1.8 }}>
                <span>
                  Username{'                '}
                  <strong>{dsName}</strong>
                </span>
                <br />
                <span>
                  API Username ID{'  '}
                  <strong>{userName}</strong>
                </span>
                <br />
                <span>
                  AccountName{'        '}
                  <strong>{dsAccountName}</strong>
                </span>
                <br />
                <span>
                  Account ID{'           '}
                  <strong>{dsAccount}</strong>
                </span>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Prompt text (matches docuSign.js lines 103-107) */}
      <Typography sx={{ fontSize: '13px', mb: 1.5 }}>
        {userName
          ? 'Connect to a different DocuSign Org:'
          : 'Are you connecting to a Developer or Production Org?'}
      </Typography>

      {/* Developer / Production buttons (matches docuSign.js lines 108-148) */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Button
          variant="contained"
          size="small"
          sx={oauthBtnSx}
          onClick={() => handleGenerateUrl('sandbox')}
          disabled={isGettingUrl}
        >
          Developer
        </Button>
        <Button
          variant="contained"
          size="small"
          sx={oauthBtnSx}
          onClick={() => handleGenerateUrl('production')}
          disabled={isGettingUrl}
        >
          Production
        </Button>
        {isAuthorizing && <CircularProgress size={16} sx={{ ml: 1 }} />}
        {isTesting && <CircularProgress size={20} sx={{ ml: 1 }} />}
        {connectionSuccess && !isTesting && (
          <CheckCircleIcon sx={{ color: '#00853E', fontSize: 28, ml: 1 }} />
        )}
      </Box>

      {/* Getting URL spinner */}
      {isGettingUrl && (
        <Box sx={{ my: 1 }}>
          <CircularProgress size={16} />
        </Box>
      )}

      {/* OAuth link (matches docuSign.js lines 158-171) */}
      {dsOauthUrl && (
        <Box sx={{ my: 1 }}>
          <Typography sx={{ fontSize: '13px' }}>
            <strong>
              <Box
                component="span"
                sx={{ cursor: 'pointer', color: '#0066cc', textDecoration: 'underline' }}
                onClick={startOauth}
              >
                Click Here
              </Box>
            </strong>{' '}
            to authorize your {providerType} Org.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ════════════════════════════════════════════════════
//  3. EDFI SECTION
//  Matches: credSections/edFi.js
// ════════════════════════════════════════════════════

export const EdFiSection = ({ provider, creds, onChange }: CredentialSectionProps) => {
  const credentials = provider.credentials || [];

  return (
    <Box>
      {credentials.map((credType) => (
        <Box key={credType} sx={{ mt: 1 }}>
          {/* Special label handling for Ed-Fi specific fields */}
          {credType === 'apiSuiteVersion' && (
            <>
              <Typography sx={labelSx}>Ed-Fi ODS / API Suite version</Typography>
              <FormControl>
                <RadioGroup
                  row
                  value={creds[credType] || ''}
                  onChange={(e) => onChange(credType, e.target.value)}
                  sx={{ gap: 3, ml: 1 }}
                >
                  <FormControlLabel
                    value="2"
                    control={<Radio size="small" />}
                    label={<Typography sx={{ fontSize: '14px' }}>API Suite 2</Typography>}
                  />
                  <FormControlLabel
                    value="3"
                    control={<Radio size="small" />}
                    label={<Typography sx={{ fontSize: '14px' }}>API Suite 3</Typography>}
                  />
                </RadioGroup>
              </FormControl>
            </>
          )}
          {credType === 'apiVersion' && (
            <CredField
              label="Api Version"
              name={credType}
              value={creds[credType]}
              onChange={onChange}
              helperText="example: v3.4.0"
              wide
            />
          )}
          {credType !== 'apiSuiteVersion' && credType !== 'apiVersion' && (
            <CredField
              label={unCamelize(credType)}
              name={credType}
              value={creds[credType]}
              onChange={onChange}
              wide
            />
          )}
        </Box>
      ))}
    </Box>
  );
};

// ════════════════════════════════════════════════════
//  4. PEOPLESOFT SECTION
//  Matches: credSections/peoplesoft.js
// ════════════════════════════════════════════════════

export const PeopleSoftSection = ({ provider, creds, onChange }: CredentialSectionProps) => {
  const credentials = provider.credentials || [];
  const authTypes = [
    { label: 'Bearer', value: 'Bearer' },
    { label: 'Basic', value: 'Basic' },
  ];

  return (
    <Box>
      {credentials.map((credType) => (
        <Box key={credType} sx={{ mt: 1 }}>
          {credType === 'authType' ? (
            <CredField
              label={unCamelize(credType)}
              name={credType}
              value={creds[credType]}
              onChange={onChange}
              select
              options={authTypes}
            />
          ) : (
            <CredField
              label={unCamelize(credType)}
              name={credType}
              value={creds[credType]}
              onChange={onChange}
              wide
            />
          )}
        </Box>
      ))}
    </Box>
  );
};

// ════════════════════════════════════════════════════
//  5. GOOGLE SECTION
//  Matches: credSections/google.js
// ════════════════════════════════════════════════════

export const GoogleSection = ({
  provider,
  creds,
  onChange,
  showPass,
  toggleShowPass,
}: CredentialSectionProps) => {
  const credentials = provider.credentials || [];
  const CloudSQLTypes = [
    { label: 'Postgres', value: 'Postgres' },
    { label: 'MySQL', value: 'MySQL' },
  ];

  return (
    <Box>
      {credentials.length > 0 && (
        <Box sx={{ ml: 0 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1 }}>
            {provider.label || provider.type} Credentials
          </Typography>
          <Box sx={{ ml: 2 }}>
            {credentials.map((credType) => (
              <Box key={credType} sx={{ mt: 1, width: '100%' }}>
                {credType === 'json' && (
                  <CredTextarea
                    label="API key (paste json or drag-and-drop file)"
                    name={credType}
                    value={creds[credType]}
                    onChange={onChange}
                    minRows={8}
                    maxRows={16}
                  />
                )}
                {credType === 'databaseType' && (
                  <CredField
                    label={unCamelize(credType)}
                    name={credType}
                    value={creds[credType]}
                    onChange={onChange}
                    select
                    options={CloudSQLTypes}
                  />
                )}
                {credType !== 'json' && credType !== 'databaseType' && (
                  <CredField
                    label={unCamelize(credType)}
                    name={credType}
                    value={creds[credType]}
                    onChange={onChange}
                    isPassword={credType === 'password'}
                    showPassToggle={credType === 'password'}
                    showPass={showPass}
                    toggleShowPass={toggleShowPass}
                    wide
                  />
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

// ════════════════════════════════════════════════════
//  6. OAUTH KEY TOGGLE SECTION (BVA / Zoom / CanvasOAuth2)
//  Matches: credSections/bva.js, zoom.js, canvasoauth2.js
//  All three share nearly identical UI structure
// ════════════════════════════════════════════════════

export const OAuthKeyToggleSection = ({
  provider,
  creds,
  onChange,
  showPass,
  toggleShowPass,
  envName,
  envId,
  connectorKey,
  connectionSuccess,
  isTesting,
}: CredentialSectionProps) => {
  const { appId, tenantId } = useApp();
  const providerType = provider.type;
  const getTokenKey = () => {
    if (providerType === 'Zoom') return 'zoomToken';
    if (providerType === 'CanvasOAuth2') return 'canvasToken';
    return 'bvaToken';
  };
  const tokenKey = getTokenKey();

  const authenticationType = creds['authenticationType'] || 'key';

  // Filter credentials based on auth type (matches bva.js/zoom.js/canvasoauth2.js lines 78-95)
  const credentials = (provider.credentials || []).filter((o) => {
    if (authenticationType === 'key') {
      return ['refreshToken', 'accessToken', 'clientId', 'rootUrl', 'clientSecret'].includes(o);
    }
    return true;
  });

  const requiredFieldsOAuth = ['rootUrl', 'clientId', 'clientSecret'];
  const requiredFieldsKey = ['rootUrl', 'clientId', 'clientSecret'];

  const credsDisabledCheck = requiredFieldsOAuth.some(
    (c) => !Object.keys(creds).includes(c) || creds[c] === ''
  );

  // OAuth URL generation and popup flow
  const [oauthUrl, setOauthUrl] = useState<string | null>(null);
  const [isGettingUrl, setIsGettingUrl] = useState(false);
  const [getBvaOAuthUrl] = useGetBvaOAuthUrlMutation();

  const handleGenerateUrl = async () => {
    if (credsDisabledCheck) return;
    setIsGettingUrl(true);
    try {
      // Get token object for scope
      const tokenObj =
        typeof creds[tokenKey] === 'object'
          ? (creds[tokenKey] as unknown as Record<string, string>)
          : {};

      // Determine oauth type based on provider
      const getOAuthType = () => {
        if (providerType === 'Zoom') return 'zoom' as const;
        if (providerType === 'CanvasOAuth2') return 'canvas' as const;
        return 'brightspace' as const;
      };
      const oauthType = getOAuthType();

      // Call BVA-specific OAuth URL endpoint (mapper transforms to snake_case)
      const result = await getBvaOAuthUrl({
        tenantId,
        appId,
        envId,
        connectionKey: connectorKey,
        oauthType,
        data: {
          rootUrl: creds['rootUrl'] || '',
          clientId: creds['clientId'] || '',
          clientSecret: creds['clientSecret'] || '',
          scope: tokenObj.scope || '',
          connectionKey: connectorKey,
        },
      }).unwrap();

      setOauthUrl(result.url);
    } catch (error) {
      logger.error('Failed to generate OAuth URL:', error);
    } finally {
      setIsGettingUrl(false);
    }
  };

  const handleStartOauth = () => {
    if (!oauthUrl) return;
    const width = 600;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;
    window.open(
      oauthUrl,
      'OAuth',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
    );
  };

  // OAuth popup flow
  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (e.data?.lingkOauthMessage === 'complete') {
        // OAuth flow completed - credentials will be refreshed via RTK Query
        setOauthUrl(null);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    <Box>
      {credentials.length > 0 && (
        <Box>
          {/* OAuth/key toggle (matches bva.js lines 145-155) */}
          <Box sx={{ mb: 1, mt: -1 }}>
            <CredToggle
              value={authenticationType}
              options={[
                { value: 'OAuth', label: 'OAuth' },
                { value: 'key', label: 'Key' },
              ]}
              onChange={(val) => onChange('authenticationType', val)}
            />
          </Box>

          {/* Credential fields */}
          {credentials.map((credType) => {
            const isRequired =
              authenticationType === 'key'
                ? requiredFieldsKey.includes(credType)
                : requiredFieldsOAuth.includes(credType);

            // Token fields (refreshToken, accessToken, scope) use nested token object
            const isTokenField = ['refreshToken', 'accessToken', 'scope'].includes(credType);

            if (isTokenField) {
              // Token fields with nested object handling - keep manual implementation
              const tokenValue =
                (creds[tokenKey] &&
                  typeof creds[tokenKey] === 'object' &&
                  (creds[tokenKey] as unknown as Record<string, string>)?.[credType]) ||
                '';

              return (
                <Box key={credType} sx={{ mt: 1 }}>
                  <Typography sx={labelSx}>
                    {unCamelize(credType)}
                    {isRequired && authenticationType === 'key' && (
                      <Box component="span" sx={{ color: 'red' }}>
                        *
                      </Box>
                    )}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TextField
                      name={credType}
                      value={tokenValue}
                      disabled={
                        credType === 'accessToken' ||
                        (authenticationType !== 'key' && credType === 'refreshToken')
                      }
                      onChange={(e) => {
                        // Update nested token object
                        const currentToken =
                          typeof creds[tokenKey] === 'object'
                            ? { ...(creds[tokenKey] as unknown as Record<string, string>) }
                            : {};
                        currentToken[credType] = e.target.value;
                        onChange(tokenKey, currentToken as unknown as string);
                      }}
                      size="small"
                      fullWidth={false}
                      sx={wideInputSx}
                    />
                    {credType === 'accessToken' && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (tokenValue) navigator.clipboard.writeText(tokenValue);
                        }}
                      >
                        <ContentCopyIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              );
            }

            // Regular fields - use CredField
            return (
              <Box key={credType} sx={{ mt: 1 }}>
                <Typography sx={labelSx}>
                  {unCamelize(credType)}
                  {isRequired && authenticationType === 'key' && (
                    <Box component="span" sx={{ color: 'red' }}>
                      *
                    </Box>
                  )}
                </Typography>
                <CredField
                  label=""
                  name={credType}
                  value={creds[credType]}
                  onChange={onChange}
                  isPassword={credType === 'clientSecret'}
                  showPassToggle={credType === 'clientSecret'}
                  showPass={showPass}
                  toggleShowPass={toggleShowPass}
                  disabled={credType === 'accessToken'}
                  wide
                />
              </Box>
            );
          })}

          {/* OAuth Login button (matches bva.js lines 237-258) */}
          {authenticationType !== 'key' && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              {isGettingUrl && <CircularProgress size={20} />}
              {!isGettingUrl && oauthUrl && (
                <Button variant="contained" size="small" sx={oauthBtnSx} onClick={handleStartOauth}>
                  Click Here to Login
                </Button>
              )}
              {!isGettingUrl && !oauthUrl && (
                <Button
                  variant="contained"
                  size="small"
                  sx={oauthBtnSx}
                  onClick={handleGenerateUrl}
                  disabled={credsDisabledCheck}
                >
                  Login
                </Button>
              )}
              {isTesting && <CircularProgress size={20} />}
              {connectionSuccess && !isTesting && (
                <CheckCircleIcon sx={{ color: '#00853E', fontSize: 28 }} />
              )}
            </Box>
          )}
          {authenticationType === 'key' && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              {isTesting && <CircularProgress size={20} />}
              {connectionSuccess && !isTesting && (
                <CheckCircleIcon sx={{ color: '#00853E', fontSize: 28 }} />
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

// ════════════════════════════════════════════════════
//  7. SFTP SECTION
//  Matches: credSections/sftp.js
// ════════════════════════════════════════════════════

export const SFTPSection = ({
  creds,
  onChange,
  showPass,
  toggleShowPass,
}: CredentialSectionProps) => {
  const credentialType = creds['credentialType'] || 'password';

  // Filter credentials based on auth type (matches sftp.js lines 89-110)
  const showPassword = credentialType !== 'ssh-key';
  const showPrivateKey = credentialType === 'ssh-key' || credentialType === 'two-Factor Auth';
  const showPassphrase = credentialType === 'ssh-key' || credentialType === 'two-Factor Auth';

  return (
    <Box>
      {/* Auth type toggle (matches sftp.js lines 133-141) */}
      <CredToggle
        value={credentialType}
        options={[
          { value: 'password', label: 'Password' },
          { value: 'ssh-key', label: 'SSH Key' },
          { value: 'two-Factor Auth', label: '2-Factor Auth' },
        ]}
        onChange={(val) => onChange('credentialType', val)}
      />

      {/* Host */}
      <CredField label="Host" name="host" value={creds['host']} onChange={onChange} wide />
      {/* Path */}
      <CredField label="Path" name="path" value={creds['path']} onChange={onChange} wide />
      {/* Username - Backend expects 'user' not 'username' */}
      <CredField label="Username" name="user" value={creds['user']} onChange={onChange} wide />

      {/* Password (only when not ssh-key mode) */}
      {showPassword && (
        <CredField
          label="Password"
          name="password"
          value={creds['password']}
          onChange={onChange}
          isPassword={!showPass}
          showPassToggle
          showPass={showPass}
          toggleShowPass={toggleShowPass}
          wide
        />
      )}

      {/* Private Key (SSH key textarea - matches sftp.js lines 173-188) */}
      {showPrivateKey && (
        <CredTextarea
          label="SSH Key (RSA Private Key without Passphrase)"
          name="privateKey"
          value={creds['privateKey']}
          onChange={onChange}
          minRows={8}
          maxRows={12}
        />
      )}

      {/* File Naming Pattern (matches sftp.js lines 154-161) */}
      <CredField
        label="File Naming Pattern (i.e. *.* or *.csv)"
        name="filePattern"
        value={creds['filePattern'] || '*.*'}
        onChange={onChange}
        wide
      />

      {/* Passphrase (only when ssh-key or 2fa mode) */}
      {showPassphrase && (
        <CredField
          label="Passphrase"
          name="passphrase"
          value={creds['passphrase']}
          onChange={onChange}
          wide
        />
      )}
    </Box>
  );
};

// ════════════════════════════════════════════════════
//  8. WEBSOCKET SECTION
//  Matches: credSections/websocket.js
// ════════════════════════════════════════════════════

export const WebSocketSection = ({ envId }: CredentialSectionProps) => {
  const { appId, tenantId } = useApp();
  const [generateCerts, { isLoading: isGenerating }] = useGenerateCertsMutation();
  const [showDownload, setShowDownload] = useState(false);

  const handleGenerateCerts = async () => {
    try {
      await generateCerts({ tenantId, appId, envId }).unwrap();
      setShowDownload(true);
    } catch (error) {
      logger.error('Failed to generate certificates:', error);
    }
  };

  const handleDownloadCerts = () => {
    // Download certs directly from backend
    // Matches legacy downloadcerts() in environmentActions.js line 1136
    const downloadUrl = `${import.meta.env.VITE_API_URL}/${tenantId}/${appId}/${envId}/getcerts`;
    window.open(downloadUrl, '_blank');
  };

  return (
    <Box>
      {/* Header (matches websocket.js lines 35-36) */}
      <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>
        Generate Websocket Certificates
      </Typography>
      <Typography sx={{ fontSize: '10px', color: '#666', mb: 1 }}>
        For Apache Nifi Authentication
      </Typography>

      {/* Generate button (matches websocket.js lines 65-93) */}
      <Box sx={{ my: 1 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleGenerateCerts}
          disabled={isGenerating}
          sx={{ textTransform: 'none', width: 130, fontSize: '13px' }}
        >
          {isGenerating ? <CircularProgress size={14} /> : 'Generate Certs'}
        </Button>

        {showDownload && (
          <Box component="span" sx={{ ml: 3 }}>
            <strong>
              Download Certs:{' '}
              <Box
                component="span"
                sx={{ cursor: 'pointer', color: '#0066cc', textDecoration: 'underline' }}
                onClick={handleDownloadCerts}
              >
                Click Here
              </Box>
            </strong>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ════════════════════════════════════════════════════
//  9. FLAT FILE SECTION (LocalFile)
//  Matches: credSections/flatfile.js (simplified)
// ════════════════════════════════════════════════════

/**
 * Build FormData and S3 URL for presigned POST upload.
 * Matches dataProtect environmentActions.js singleFileToS3 exactly.
 * Backend returns { policy, signature, guids } - we parse policy and build FormData.
 */
function buildS3UploadFormData(
  policyResp: { policy: string; signature: string },
  tenantKey: string,
  appKey: string,
  envId: string,
  fileName: string,
  file: File
): { formData: FormData; url: string } {
  const p = JSON.parse(atob(policyResp.policy)) as {
    conditions: Array<Record<string, string>>;
  };
  const pFind = (s: string): string =>
    p.conditions.find((pf) => Object.keys(pf).includes(s))?.[s] ?? '';
  const fd = new FormData();
  let encodedFileName = fileName;
  if (fileName.includes('+')) {
    encodedFileName = encodeURIComponent(fileName);
  }
  fd.append('key', `${tenantKey}/${appKey}/${envId}/${encodedFileName}`);
  fd.append('policy', policyResp.policy);
  fd.append('acl', pFind('acl'));
  fd.append('success_action_status', pFind('success_action_status'));
  fd.append('x-amz-credential', pFind('x-amz-credential'));
  fd.append('x-amz-algorithm', pFind('x-amz-algorithm'));
  fd.append('x-amz-date', pFind('x-amz-date'));
  fd.append('x-amz-signature', policyResp.signature);
  // Explicit filename required - without it browser may send "blob" and S3 receives 0 bytes
  fd.append('file', file, file.name);
  const bucket = pFind('bucket');
  const url = `https://${bucket}.s3.amazonaws.com/`;
  return { formData: fd, url };
}

const ROWS_PER_PAGE = 20;

export const FlatFileSection = ({ envName, envId }: CredentialSectionProps) => {
  const { appId, app, tenantId } = useApp();
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [deleteModalFileName, setDeleteModalFileName] = useState<string | null>(null);

  const { data: tenant } = useGetTenantQuery(tenantId, { skip: !tenantId });

  const { data: fileCount, isLoading: isLoadingCount } = useGetFileSchemaCountQuery(
    { tenantId, appId, envName },
    { skip: !envName }
  );

  const count = fileCount?.count ?? 0;
  // Fetch all files when count > 0 so we can sort globally (not just per page)
  const { data: allFileSchemas, isLoading: isLoadingFiles } = useGetFileSchemaQuery(
    {
      tenantId,
      appId,
      envName,
      limit: count > 0 ? count : ROWS_PER_PAGE,
      offset: 0,
    },
    { skip: !envName || (count === 0 && !isLoadingCount) }
  );

  // Sort all files alphabetically by name, then paginate client-side
  const sortedFileSchemas = allFileSchemas
    ? [...allFileSchemas].sort((a, b) =>
        (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
      )
    : [];
  const fileSchemas = sortedFileSchemas.slice(
    page * ROWS_PER_PAGE,
    page * ROWS_PER_PAGE + ROWS_PER_PAGE
  );

  const [getUploadPolicy] = useGetUploadPolicyMutation();
  const [saveCredentials] = useSaveCredentialsMutation();
  const [deleteFile, { isLoading: isDeleting }] = useDeleteFileMutation();
  const [getDownloadPolicy] = useLazyGetDownloadPolicyQuery();

  const ACCEPT_ATTR =
    '.csv,.json,.xlsx,.xls,.txt,text/csv,application/json,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/plain';

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setDroppedFiles(Array.from(e.target.files));
      setUploadError(null);
    }
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files) {
      setDroppedFiles((prev) => [...prev, ...Array.from(e.dataTransfer.files)]);
      setUploadError(null);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleUpload = async () => {
    if (droppedFiles.length === 0 || !tenant?.key || !app?.uniqueKey) return;
    setUploading(true);
    setUploadError(null);

    try {
      // Batch: one uploadpolicy for all files (matches dataProtect setCsvFields exactly)
      // byteSize is required - backend stores it; otherwise files show 0 Bytes
      const policyResp = await getUploadPolicy({
        tenantId,
        appId,
        envId,
        name: 'localfile',
        resources: droppedFiles.map((f) => ({ name: f.name, byteSize: f.size })),
        providerType: 'LocalFile',
      }).unwrap();

      // Upload each file to S3 (same policy works for all - matches uploadFilesToS3)
      for (const file of droppedFiles) {
        const { formData, url } = buildS3UploadFormData(
          policyResp,
          tenant.key,
          app.uniqueKey,
          envId,
          file.name,
          file
        );

        const response = await fetch(url, {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`Upload failed: ${response.status}`);
        }
      }

      // postCredentials step - save guids to backend (matches dataProtect setCsvFields)
      await saveCredentials({
        tenantId,
        appId,
        envId,
        key: 'LocalFile',
        data: {
          credentialType: 'LocalFile',
          providerLabel: 'LocalFile',
          key: 'LocalFile',
          credentialsJson: policyResp.guids as unknown as Record<string, unknown>,
          ApiAccessible: false,
        },
      }).unwrap();

      const uploadedCount = droppedFiles.length;
      setDroppedFiles([]);
      // Use initiate() instead of refetch() - refetch throws if query was skipped (never started)
      if (envName) {
        const cnt = (fileCount?.count ?? 0) + uploadedCount;
        await Promise.all([
          dispatch(
            environmentsApi.endpoints.getFileSchema.initiate(
              {
                tenantId,
                appId,
                envName,
                limit: cnt > 0 ? cnt : ROWS_PER_PAGE,
                offset: 0,
              },
              { forceRefetch: true }
            )
          ),
          dispatch(
            environmentsApi.endpoints.getFileSchemaCount.initiate(
              { tenantId, appId, envName },
              { forceRefetch: true }
            )
          ),
        ]);
      }
    } catch (error) {
      logger.error('Failed to upload files:', error);
      setUploadError('Sorry. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (e: React.MouseEvent, fileName: string) => {
    e.preventDefault();
    try {
      const url = await getDownloadPolicy({ tenantId, appId, envId, fileName }).unwrap();
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      logger.error('Failed to get download URL:', err);
    }
  };

  const handleDeleteClick = (fileName: string) => setDeleteModalFileName(fileName);

  const handleDeleteConfirm = async () => {
    if (!deleteModalFileName) return;
    try {
      await deleteFile({ tenantId, appId, envId, fileName: deleteModalFileName }).unwrap();
      setDeleteModalFileName(null);
      if (envName) {
        const cnt = fileCount?.count ?? 0;
        await Promise.all([
          dispatch(
            environmentsApi.endpoints.getFileSchema.initiate(
              {
                tenantId,
                appId,
                envName,
                limit: cnt > 0 ? cnt : ROWS_PER_PAGE,
                offset: 0,
              },
              { forceRefetch: true }
            )
          ),
          dispatch(
            environmentsApi.endpoints.getFileSchemaCount.initiate(
              { tenantId, appId, envName },
              { forceRefetch: true }
            )
          ),
        ]);
      }
      if (page > 0 && (!fileSchemas || fileSchemas.length <= 1)) setPage((p) => Math.max(0, p - 1));
    } catch (err) {
      logger.error('Failed to delete file:', err);
    }
  };

  const totalPages = Math.ceil(count / ROWS_PER_PAGE) || 1;

  // When current page is empty after delete (e.g. was on page 2 with 1 file, deleted it), go to prev page
  useEffect(() => {
    if (!isLoadingFiles && count > 0 && fileSchemas && fileSchemas.length === 0 && page > 0) {
      setPage((p) => Math.max(0, p - 1));
    }
  }, [isLoadingFiles, count, fileSchemas, page]);

  return (
    <Box>
      {/* Error message (matches legacy "Sorry. Please try again.") */}
      {uploadError && (
        <Typography sx={{ color: '#c9302c', fontWeight: 'bold', mb: 2 }}>{uploadError}</Typography>
      )}

      {/* Dropzone area (matches flatfile.js lines 363-379) */}
      <Box
        sx={{
          display: 'inline-block',
          border: '2px dashed #ccc',
          borderRadius: '8px',
          padding: '20px 30px',
          textAlign: 'center',
          cursor: 'pointer',
          minWidth: 300,
          '&:hover': { borderColor: '#999' },
        }}
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <Typography sx={{ fontSize: '13px', color: '#666' }}>
          Upload your CSV, JSON, or Excel files here.
        </Typography>
        <Typography sx={{ fontSize: '12px', color: '#999', mt: 0.5 }}>
          Drop one or more files here, or click to select files to upload.
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPT_ATTR}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </Box>

      {/* Dropped files list (pending upload) */}
      {droppedFiles.length > 0 && (
        <Box sx={{ mt: 2, display: 'inline-block', verticalAlign: 'top', ml: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ fontSize: '16px' }}>
              Dropped files
            </Typography>
            <Button
              variant="contained"
              size="small"
              sx={oauthBtnSx}
              onClick={handleUpload}
              disabled={uploading || !tenant?.key || !app?.uniqueKey}
            >
              {uploading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : 'Upload'}
            </Button>
          </Box>
          <Box component="ul" sx={{ p: 0, mt: 1 }}>
            {droppedFiles.map((f, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Box component="li" key={i} sx={{ listStyle: 'none', fontSize: '13px' }}>
                {f.name} - {formatBytes(f.size)}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Uploaded files table (matches dataProtect flatfile.js) */}
      {isLoadingCount && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      )}
      {!isLoadingCount && count > 0 && (
        <Box sx={{ mt: 2, width: '100%', maxWidth: 720 }}>
          <Box
            sx={{
              mb: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography component="strong" sx={{ fontSize: '14px' }}>
              {count} file{count === 1 ? '' : 's'} uploaded
            </Typography>
            {totalPages > 1 && (
              <TablePagination
                component="div"
                count={count}
                page={page}
                onPageChange={(_e, newPage) => setPage(newPage)}
                rowsPerPage={ROWS_PER_PAGE}
                rowsPerPageOptions={[ROWS_PER_PAGE]}
                showFirstButton
                showLastButton
                sx={{ border: 'none' }}
              />
            )}
          </Box>
          <TableContainer
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              maxHeight: 360,
              overflow: 'auto',
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Size</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Date uploaded</TableCell>
                  <TableCell sx={{ width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoadingFiles && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ py: 3, textAlign: 'center' }}>
                      <CircularProgress size={20} />
                    </TableCell>
                  </TableRow>
                )}
                {!isLoadingFiles && (!fileSchemas || fileSchemas.length === 0) && (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}
                    >
                      No files on this page
                    </TableCell>
                  </TableRow>
                )}
                {!isLoadingFiles &&
                  fileSchemas &&
                  fileSchemas.length > 0 &&
                  fileSchemas.map((f) => (
                    <TableRow key={f.name} hover>
                      <TableCell>
                        <Link
                          component="button"
                          variant="body2"
                          onClick={(e) => handleDownload(e, f.name)}
                          sx={{ cursor: 'pointer', textAlign: 'left' }}
                        >
                          {f.name}
                        </Link>
                      </TableCell>
                      <TableCell>{formatBytes(f.size ?? 0)}</TableCell>
                      <TableCell>
                        {f.lastModified ? format(new Date(f.lastModified), 'MMM do, h:mm a') : '—'}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(f.name)}
                          sx={{ p: 0.5 }}
                          aria-label={`Delete ${f.name}`}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
      {!isLoadingCount && count === 0 && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Typography component="strong" sx={{ fontSize: '14px', color: 'text.secondary' }}>
            No files found
          </Typography>
        </Box>
      )}

      <DeleteConfirmDialog
        open={!!deleteModalFileName}
        onClose={() => setDeleteModalFileName(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete File"
        itemName={deleteModalFileName ?? undefined}
        message={
          deleteModalFileName
            ? `Are you sure you want to delete ${deleteModalFileName}?`
            : undefined
        }
        confirmText="Delete"
        isDeleting={isDeleting}
      />
    </Box>
  );
};

// ════════════════════════════════════════════════════
//  10. ADAPTER SECRET SECTION
//  Matches: credSections/adaptersecret.js
//  Appended to default section for adapter types
// ════════════════════════════════════════════════════

export const AdapterSecretSection = ({
  provider: _provider,
  envName,
}: {
  provider: IntegrationProvider;
  envName: string;
}) => {
  const { appId, app: appData, tenantId } = useApp();
  const [generateSecret, { isLoading: isGenerating }] = useGenerateAdapterSecretMutation();
  const { data: credentials } = useGetCredentialsQuery({ tenantId, appId, envName });
  const { data: tenantData } = useGetTenantQuery(tenantId, { skip: !tenantId });
  const [secret, setSecret] = useState<string | null>(null);

  // Load existing adapter secret from credentials list (matches envBars.js lines 281-286)
  useEffect(() => {
    if (credentials && !secret) {
      const adapterSecretCred = credentials.find((c) => c.credentialType === 'AdapterSecret');
      if (adapterSecretCred?.credentials) {
        setSecret(adapterSecretCred.credentials);
      }
    }
  }, [credentials, secret]);

  // Build client key: client.secret.{tenantKey}.{accountKey}
  // Matches legacy: `client.secret.${tenantKey}.${accountKey}` (CredSections.js line 419)
  const tenantKey = tenantData?.key || tenantId;
  const accountKey = appData?.uniqueKey || appId;
  const clientKey = `client.secret.${tenantKey}.${accountKey}`;

  const handleGenerate = async () => {
    try {
      const result = await generateSecret({
        tenantId,
        appId,
        envName,
      }).unwrap();
      setSecret(result.credentials);
    } catch {
      // Error handling
    }
  };

  return (
    <Box
      sx={{
        mt: 3,
        pt: 3,
        borderTop: '1px solid #e0e0e0',
      }}
    >
      <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 2, color: '#333' }}>
        Adapter Credentials
      </Typography>

      {secret ? (
        <Box sx={{ ml: 2 }}>
          {/* Client Key */}
          <Box sx={{ mb: 2 }}>
            <Typography sx={labelSx}>Client Key</Typography>
            <TextField
              value={clientKey}
              size="small"
              fullWidth
              sx={{
                maxWidth: 500,
                '& .MuiInputBase-input': { fontSize: '13px', py: '6px', px: '12px' },
                '& .MuiOutlinedInput-root': { borderRadius: '4px' },
              }}
              slotProps={{ input: { readOnly: true } }}
            />
          </Box>

          {/* Client Secret */}
          <Box sx={{ mb: 1 }}>
            <Typography sx={labelSx}>Client Secret</Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <TextField
                value={secret}
                size="small"
                sx={{
                  flex: 1,
                  maxWidth: 500,
                  '& .MuiInputBase-input': { fontSize: '13px', py: '6px', px: '12px' },
                  '& .MuiOutlinedInput-root': { borderRadius: '4px' },
                }}
                slotProps={{ input: { readOnly: true } }}
              />
              <CopyButton value={secret} size="small" label="Copy" />
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{ ml: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography sx={{ fontSize: '13px', color: '#666' }}>
            No adapter secret found. Generate one to enable API access.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={handleGenerate}
            disabled={isGenerating}
            sx={{ textTransform: 'none', fontSize: '12px', minWidth: 180 }}
          >
            {isGenerating ? (
              <>
                <CircularProgress size={14} sx={{ mr: 1 }} />
                Generating...
              </>
            ) : (
              'Generate Adapter Secret'
            )}
          </Button>
        </Box>
      )}
    </Box>
  );
};

// ════════════════════════════════════════════════════
//  11. DEFAULT SECTION
//  Matches: credSections/index.js lines 418-618 (default branch)
// ════════════════════════════════════════════════════

export const DefaultSection = ({
  provider,
  creds,
  onChange,
  showPass,
  toggleShowPass,
  envName,
  connectionSuccess,
  isTesting,
}: CredentialSectionProps) => {
  const isSFTP = provider.type === 'SFTP';
  const isAWS = provider.type.startsWith('AWS');
  const isMSSQL = provider.type.startsWith('MSSQL');
  const noRootUrl = isSFTP || isAWS || isMSSQL;
  const credentialFields = provider.credentials?.filter((f) => f !== 'Oauth') || [];

  const isAdapter =
    provider.metadataEndpoint === 'testconnection' ||
    provider.metadataEndpoint === 'adaptermetadata';

  const isEthos = provider.type === 'Ethos';

  // Check if provider has 'url' field in credentials - if so, don't render rootUrl
  // This prevents duplicate URL fields for HTTP connector (backend expects 'url', not 'rootUrl')
  const hasUrlField = credentialFields.includes('url');
  const shouldRenderRootUrl = !noRootUrl && !hasUrlField;

  // If provider has NO credentials AND is not an adapter, show environment variables message
  // Adapters without credential fields still render the rootUrl + AdapterSecretSection
  // Matches legacy Connector.js: showProviderForm = (providerConfig.credentials) || isAdapter
  const hasNoCredentials = !provider.credentials || provider.credentials.length === 0;

  if (hasNoCredentials && !isAdapter) {
    return (
      <Box>
        <Typography sx={{ fontSize: '13px', color: '#666', mb: 2 }}>
          This connector uses environment variables for configuration.
          <br />
          Go to <strong>Manage Environment Variables</strong> in the Connections tab to configure
          this connector.
        </Typography>
      </Box>
    );
  }

  // AmazonRDS database type options
  const AmazonRDSdbs = [
    { label: 'PostgreSQL', value: 'PostgreSQL' },
    { label: 'MySQL', value: 'MySQL' },
  ];

  return (
    <Box>
      {/* Data Source Endpoint Section - Only render if provider doesn't have 'url' in credentials */}
      {shouldRenderRootUrl && (
        <Box sx={{ mb: 1 }}>
          <Typography sx={labelSx}>Data Source Endpoint</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              name="rootUrl"
              value={creds['rootUrl'] || 'https://'}
              onChange={(e) => onChange('rootUrl', e.target.value)}
              size="small"
              sx={wideInputSx}
            />
            {isTesting && <CircularProgress size={20} sx={{ flexShrink: 0 }} />}
            {connectionSuccess && !isTesting && (
              <CheckCircleIcon sx={{ color: '#00853E', fontSize: 28, flexShrink: 0 }} />
            )}
          </Box>
        </Box>
      )}

      {/* Credential fields (matches index.js lines 471-604) */}
      {credentialFields.length > 0 && (
        <Box sx={{ mt: 2 }}>
          {shouldRenderRootUrl && (
            <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 2, color: '#333' }}>
              {provider.label || provider.type} Credentials
            </Typography>
          )}
          <Box sx={{ ml: shouldRenderRootUrl ? 2 : 0 }}>
            {credentialFields.map((credType) => (
              <Box key={credType} sx={{ mb: 2 }}>
                {credType === 'databaseType' ? (
                  <CredField
                    label={unCamelize(credType)}
                    name={credType}
                    value={creds[credType]}
                    onChange={onChange}
                    select
                    options={AmazonRDSdbs}
                    wide
                  />
                ) : (
                  <CredField
                    label={unCamelize(credType)}
                    name={credType}
                    value={creds[credType]}
                    onChange={onChange}
                    isPassword={credType === 'password'}
                    showPassToggle={credType === 'password'}
                    showPass={showPass}
                    toggleShowPass={toggleShowPass}
                    wide
                  />
                )}
              </Box>
            ))}

            {/* Ethos account info (matches index.js lines 567-601) */}
            {isEthos && creds['name'] && creds['name'] !== '' && (
              <Box
                sx={{
                  p: 1.5,
                  mt: 1.5,
                  border: '1px solid grey',
                  borderRadius: '8px',
                }}
              >
                <Typography sx={{ fontSize: '16px', fontWeight: 'bold', mb: 0.5 }}>
                  Account Information
                </Typography>
                <Typography sx={{ ...labelSx, fontWeight: 'normal', display: 'inline' }}>
                  Name:
                </Typography>{' '}
                {creds['name']}
                <br />
                <Typography sx={{ ...labelSx, fontWeight: 'normal', display: 'inline' }}>
                  Label:
                </Typography>{' '}
                {creds['label']}
                <br />
                <Typography sx={{ ...labelSx, fontWeight: 'normal', display: 'inline' }}>
                  Account Id:
                </Typography>{' '}
                {creds['accountId']}
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Adapter Secret (matches index.js lines 606-617) */}
      {isAdapter && <AdapterSecretSection provider={provider} envName={envName} />}
    </Box>
  );
};

// ════════════════════════════════════════════════════
//  CREDENTIAL SECTION ROUTER
//  Main export — picks the right section based on provider type
// ════════════════════════════════════════════════════

export const CredentialSectionRouter = (props: CredentialSectionProps) => {
  const { provider } = props;
  const providerType = provider.type;

  // OAuth: credentials array is exactly ["Oauth"] (matches index.js lines 205-208)
  const isOauth =
    provider.credentials &&
    provider.credentials.length === 1 &&
    provider.credentials[0] === 'Oauth';

  const isDocuSign = providerType === 'DocuSign';
  const isEdFi = providerType.toLowerCase() === 'edfi';
  const isPeopleSoftV2 = providerType === 'PeopleSoftV2';
  const isFlatFile = providerType === 'LocalFile';
  const isBVA = providerType === 'BrightSpaceValenceApi';
  const isGoogle = providerType.startsWith('Google');
  const isSFTP = providerType === 'SFTP';
  const isWebSocket = providerType === 'LingkWebSocket';
  const isZoom = providerType === 'Zoom';
  const isCanvasOAuth2 = providerType === 'CanvasOAuth2';

  if (isOauth) return <OAuthSection {...props} />;
  if (isDocuSign) return <DocuSignSection {...props} />;
  if (isEdFi) return <EdFiSection {...props} />;
  if (isPeopleSoftV2) return <PeopleSoftSection {...props} />;
  if (isFlatFile) return <FlatFileSection {...props} />;
  if (isBVA || isZoom || isCanvasOAuth2) return <OAuthKeyToggleSection {...props} />;
  if (isGoogle) return <GoogleSection {...props} />;
  if (isSFTP) return <SFTPSection {...props} />;
  if (isWebSocket) return <WebSocketSection {...props} />;
  return <DefaultSection {...props} />;
};
